/* eslint-disable @typescript-eslint/ban-types */
import React, { ReactElement } from "react";
import { NextPage } from "next";

import useRouter from "./useRouter";
import useGlobalState from "./useGlobalState";
import { redirectToLoginPage } from "../api/phpApi/baseQuery";
import CustomSpinner from "components/CustomSpinner";
import { CustomAppPage } from "src/types/types";

export function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function DefaultLoadingFallback(): ReactElement {
  return (
    <CustomSpinner
      position="fixed"
      left="50%"
      top="50%"
      transform="translate(-50%, -50%)"
    />
  );
}

/**
 * Support client-side conditional redirecting based on the user's
 * authenticated state.
 *
 * @param WrappedComponent The component that this functionality
 * will be added to.
 * @param LoadingComponent The component that will be rendered while
 * the auth state is loading.
 * @param expectedAuth Whether the user should be authenticated for
 * the component to be rendered.
 */
export default function withAuthRedirect<
  CP extends JSX.IntrinsicAttributes = {},
  IP = CP,
>({
  WrappedComponent,
  LoadingComponent = DefaultLoadingFallback,
  expectedAuth,
  hasHiddenFooter,
}: {
  WrappedComponent: CustomAppPage<CP, IP>;
  LoadingComponent?: CustomAppPage;
  expectedAuth: boolean;
  hasHiddenFooter?: boolean;
}): NextPage<CP, IP> {
  const WithAuthRedirectWrapper: CustomAppPage<CP, IP> = (props) => {
    const { navigateTo } = useRouter();
    const { user, isLoaded } = useGlobalState(); // isLoading: isUserLoading

    if (!isLoaded) {
      return <LoadingComponent />;
    }

    if (isBrowser() && expectedAuth !== Boolean(user)) {
      navigateTo(redirectToLoginPage());

      return <></>;
    }

    return <WrappedComponent {...props} />;
  };

  if (hasHiddenFooter) {
    WithAuthRedirectWrapper.hasHiddenFooter = hasHiddenFooter;
  }

  return WithAuthRedirectWrapper;
}
